import React, { useState } from 'react';
import { AlertTriangle, Phone, X, MapPin } from 'lucide-react';
import { useCountry } from '@/contexts/CountryContext';
import { EMERGENCY_NUMBERS, TOURIST_ESSENTIALS } from '@/data/touristEssentials';
import { cn } from '@/lib/utils';

export const EmergencyButton = () => {
  const [open, setOpen] = useState(false);
  const { country } = useCountry();
  const numbers = EMERGENCY_NUMBERS[country.code] || EMERGENCY_NUMBERS.albania;

  // Nearest essentials in current country
  const nearest = TOURIST_ESSENTIALS
    .filter(e => e.country === country.code && (e.type === 'hospital' || e.type === 'pharmacy'))
    .slice(0, 3);

  const actions = [
    { label: 'Police',    value: numbers.police,    color: '#1E40AF', emoji: '🚔' },
    { label: 'Ambulance', value: numbers.ambulance, color: '#DC2626', emoji: '🚑' },
    { label: 'Fire',      value: numbers.fire,      color: '#F97316', emoji: '🚒' },
    { label: 'Tourist help', value: numbers.tourist, color: '#00AA6C', emoji: '🆘' },
  ];

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label="Emergency contacts"
        data-testid="emergency-fab"
        className={cn(
          'fixed bottom-24 right-6 z-40 w-12 h-12 rounded-full bg-[#DC2626] hover:bg-[#B91C1C] text-white shadow-2xl shadow-[#DC2626]/40 flex items-center justify-center transition-all hover:scale-105',
          open && 'opacity-0 pointer-events-none scale-90'
        )}
      >
        <AlertTriangle className="w-5 h-5" />
        <span className="absolute inset-0 rounded-full bg-[#DC2626] animate-ping opacity-30" />
      </button>

      <div className={cn('fixed inset-0 bg-black/40 backdrop-blur-sm z-50 transition-opacity', open ? 'opacity-100' : 'opacity-0 pointer-events-none')} onClick={() => setOpen(false)} />
      <div
        className={cn(
          'fixed bottom-0 left-0 right-0 sm:bottom-6 sm:right-6 sm:left-auto z-50 w-full sm:w-[400px] bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden transition-all duration-300',
          open ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0 pointer-events-none'
        )}
        data-testid="emergency-panel"
      >
        <div className="bg-[#DC2626] text-white px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            <div>
              <div className="font-heading font-bold">Emergency in {country.name}</div>
              <div className="text-[10px] text-white/80 uppercase tracking-wider">Tap to call directly</div>
            </div>
          </div>
          <button onClick={() => setOpen(false)} aria-label="Close" className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 grid grid-cols-2 gap-2.5">
          {actions.map(a => (
            <a
              key={a.label}
              href={`tel:${a.value.replace(/\s/g, '')}`}
              className="flex flex-col items-center justify-center py-4 rounded-xl text-white font-bold gap-1"
              style={{ background: a.color }}
              data-testid={`emergency-${a.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <span className="text-2xl">{a.emoji}</span>
              <span className="text-sm">{a.label}</span>
              <span className="text-xs font-mono opacity-90">{a.value}</span>
            </a>
          ))}
        </div>
        {nearest.length > 0 && (
          <div className="border-t border-gray-100 p-4">
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2">Nearest medical help</div>
            <div className="space-y-2">
              {nearest.map(n => (
                <a key={n.id} href={`tel:${n.phone.replace(/\s/g, '')}`} className="flex items-start gap-2.5 p-2 rounded-lg hover:bg-gray-50">
                  <span className="text-xl mt-0.5">{n.type === 'hospital' ? '🏥' : '💊'}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-gray-900 line-clamp-1">{n.name}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> {n.address}</div>
                  </div>
                  <Phone className="w-4 h-4 text-[#DC2626] mt-1 shrink-0" />
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};
